import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { parseAgentTabRoute } from '../routing/browser-tab-routes';
import { AgentDataService } from '../services/agent-data.service';
import { ApiRuntimeConfig } from '../services/api-runtime-config';
import { AuthSessionService } from '../services/auth-session.service';
import {
  BrowserTabPopoutService,
  normalizeRoutePath,
} from '../services/browser-tab-popout.service';
import { BrowserTabService } from '../services/browser-tab.service';

/** Loads page-level mock defaults (browser tabs, etc.) before the app renders. */
export function initializeMockPageDefaults(): () => Promise<void> {
  return async () => {
    const data = inject(AgentDataService);
    const browserTabs = inject(BrowserTabService);
    const popout = inject(BrowserTabPopoutService);
    const auth = inject(AuthSessionService);
    const router = inject(Router);
    const apiConfig = inject(ApiRuntimeConfig);

    if (popout.hasPendingPopoutLaunch()) {
      const pendingRouteUrl = popout.getPendingPopoutRouteUrl();
      if (
        pendingRouteUrl &&
        normalizeRoutePath(router.url) !== normalizeRoutePath(pendingRouteUrl)
      ) {
        await router.navigateByUrl(pendingRouteUrl);
      }

      if (popout.tryHydrateFromPendingLaunch(auth, router)) {
        return;
      }

      // Popout marker present but not consumable here — never load the parent session.
      return;
    }

    if (apiConfig.mockApiAvailable()) {
      const defaults = await firstValueFrom(data.getBrowserTabDefaults());
      browserTabs.setTabDefaults(defaults);

      browserTabs.applyDefaults(defaults);
    } else {
      browserTabs.reset();
    }

    if (!apiConfig.useMocks()) {
      browserTabs.sanitizeForLiveDealerMode();
    }

    browserTabs.syncActiveTabFromUrl(router.url);

    const parsed = parseAgentTabRoute(router.url);
    if (parsed && !browserTabs.hasTab(parsed.tabId)) {
      await router.navigateByUrl(`/${browserTabs.activeTabId()}/events`);
    }
  };
}
